/* PreviewRail — right column of the Rule Builder.
 *
 * Three stacked panels:
 *   • English    — predicateToText(predicate) — what the designer reads.
 *   • SPARQL     — ruleToSparql(predicate) — real SPARQL 1.1 grammar.
 *   • Validator  — validateRule(predicate) — green/red strip + issues.
 *
 * Stays in sync with the Canvas via the same Predicate prop.
 */

import { useMemo } from "react";
import { Code2, FileText, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import type { Predicate } from "../../../types/rule";
import { predicateToText } from "../../../lib/predicateRender";
import { ruleToSparql } from "../../../lib/ruleToSparql";
import { validateRule } from "../../../lib/ruleValidate";
import { cls } from "../../../lib/cls";

interface PreviewRailProps {
  predicate: Predicate;
}

export function PreviewRail({ predicate }: PreviewRailProps): JSX.Element {
  const english = useMemo(() => predicateToText(predicate), [predicate]);
  const sparql = useMemo(() => ruleToSparql(predicate), [predicate]);
  const validation = useMemo(() => validateRule(predicate), [predicate]);

  return (
    <aside className="space-y-3 sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
      <div className="panel p-4">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-3.5 h-3.5 text-ink-500" />
          <div className="eyebrow">In plain English</div>
        </div>
        <p className="text-[13px] text-ink-950 leading-snug">{english}</p>
      </div>

      <div className="panel p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Code2 className="w-3.5 h-3.5 text-ink-500" />
            <div className="eyebrow">SPARQL 1.1 · {sparql.form}</div>
          </div>
          <span className="text-[10px] text-ink-500 font-mono">{sparql.query.split("\n").length} lines</span>
        </div>
        <pre className="text-[11px] font-mono bg-cream-deeper rounded-lg p-3 overflow-x-auto leading-snug text-ink-950 whitespace-pre">{sparql.query}</pre>
        <p className="text-[10px] text-ink-500 mt-2 leading-snug">
          Compiled from the typed AST · prefixes resolved from <span className="font-mono">codegen.json</span>.
          The agent's typed evaluator runs the same query against the project graph.
        </p>
      </div>

      <div className={cls("panel p-4", validation.ok ? "ring-1 ring-fern/40" : "ring-1 ring-foyr/40")}>
        <div className="flex items-center gap-2 mb-2">
          {validation.ok ? (
            <><CheckCircle2 className="w-3.5 h-3.5 text-fern" /><div className="eyebrow text-fern">Validator · ok</div></>
          ) : (
            <><XCircle className="w-3.5 h-3.5 text-foyr" /><div className="eyebrow text-foyr">Validator · fix before promote</div></>
          )}
        </div>
        {validation.errors.length === 0 && validation.warnings.length === 0 ? (
          <p className="text-[12px] text-ink-700">Ontology-typed and logically sound. Ready to test.</p>
        ) : (
          <ul className="space-y-1.5">
            {validation.errors.map((e, i) => (
              <li key={`e-${i}`} className="flex items-start gap-1.5 text-[12px] text-foyr-deep">
                <XCircle className="w-3 h-3 mt-0.5 shrink-0" /> <span>{e.message}</span>
              </li>
            ))}
            {validation.warnings.map((w, i) => (
              <li key={`w-${i}`} className="flex items-start gap-1.5 text-[12px] text-ink-700">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> <span>{w.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
